import { Controller } from "@hotwired/stimulus"

/*
  TabsController

  A generic client-side tab switcher that reads its initial state from a URL
  query parameter and updates the URL when the user switches tabs.

  ## Query param usage:
  - `?key_tab=app`: opens the "app" tab on page load

  ## Example:

  ```
  <div data-controller="tabs" data-tabs-param-name-value="key_tab" data-tabs-default-tab-value="org">
    <ul class="c-tabs">
      <li>
        <a href="/path?key_tab=org" class="c-tabs__link"
          data-tabs-target="tab" data-tab-value="org"
          data-text="Organization Push key" data-action="tabs#switch">
          Organization Push key
        </a>
      </li>
      <li>
        <a href="/path?key_tab=app" class="c-tabs__link"
          data-tabs-target="tab" data-tab-value="app"
          data-text="App environment Push key" data-action="tabs#switch">
          App environment Push key
        </a>
      </li>
    </ul>
    <div data-tabs-target="panel" data-tab-value="org">
      Organization content
    </div>
    <div data-tabs-target="panel" data-tab-value="app">
      App content
    </div>
  </div>
  ```
*/

export default class extends Controller {
  static targets = ["tab", "panel"]

  static values = {
    paramName: { type: String, default: "tab" },
    defaultTab: { type: String, default: "" },
  }

  connect() {
    const params = new URLSearchParams(window.location.search)
    const paramTab = params.get(this.paramNameValue)
    const initialTab = paramTab || this.defaultTabValue || this.tabTargets[0]?.dataset.tabValue
    this.activate(initialTab)
  }

  switch(event) {
    event.preventDefault()
    const tabValue = event.currentTarget.dataset.tabValue
    this.activate(tabValue)
    this.updateUrl(tabValue)
  }

  activate(tabValue) {
    this.tabTargets.forEach((tab) => {
      if (tab.dataset.tabValue === tabValue) {
        tab.setAttribute("data-state", "active")
      } else {
        tab.removeAttribute("data-state")
      }
    })

    this.panelTargets.forEach((panel) => {
      if (panel.dataset.tabValue === tabValue) {
        panel.classList.remove("hidden")
      } else {
        panel.classList.add("hidden")
      }
    })
  }

  updateUrl(tabValue) {
    const url = new URL(window.location)
    url.searchParams.set(this.paramNameValue, tabValue)
    history.replaceState({}, "", url)
  }
}
